import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';

import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';

import FMPSectionHeader from '../../components/ui/FMPSectionHeader';
import FMPStatusIcon from '../../components/ui/FMPStatusIcon';
import InventoryBalanceStatusContainer from '../../containers/inventory/InventoryBalanceStatus';
import { getStatus } from '../../modules/inventory/inventoryBalanceStatus';

const styles = (theme) => {
	return {
		innerPaper: {
			padding: 10,
			paddingTop: 5,
			textAlign: 'left',
			fontSize: 14,
			[theme.breakpoints.up('md')]: {
				padding: 40,
				paddingTop: 30,
			}
		},
		label: {
			paddingRight: 5,
			textTransform: 'uppercase',
			fontSize: '85%',
			color: '#555',
		},
		healthReasons: {
			background: '#FFF8E1',
			border: '1px solid #FFE082',
			borderRadius: 4,
			padding: '8px 10px',
			marginBottom: 10,
			fontSize: 13,
		},
		errorValue: {
			color: '#f44336',
		},
		scheduleNote: {
			fontSize: 13,
			color: '#666',
		},
	};
};

//- Minutes -> something a person reads without counting zeros.
function formatAgeMinutes(minutes) {
	if (minutes == null) {
		return 'never';
	}
	if (minutes < 60) {
		return `${minutes}m ago`;
	}
	const hours = Math.floor(minutes / 60);
	if (hours < 24) {
		return `${hours}h ${minutes % 60}m ago`;
	}
	return `${Math.floor(hours / 24)}d ${hours % 24}h ago`;
}

class InventoryDashboard extends Component {
	constructor(props) {
		super(props);
		this.state = {
			status: undefined,
			isLoading: true,
		};
	}

	componentDidMount() {
		getStatus({
			callback: (err, r) => {
				if (err) {
					this.setState({ isLoading: false });
					return this.props.showErrors(_.isArray(err) ? err : [err]);
				}
				this.setState({ status: r, isLoading: false });
			},
		});
	}

	renderRow = (label, value, isError) => {
		const { classes } = this.props;
		return (
			<div style={{ marginTop: 4 }} key={label}>
				<span className={classes.label}>{label}: </span>
				<span className={isError ? classes.errorValue : undefined}>{value}</span>
			</div>
		);
	};

	renderSyncPanel = () => {
		const { classes, canRwContracts } = this.props;
		const { status, isLoading } = this.state;

		if (isLoading) {
			return <div>Loading inventory balance sync status...</div>;
		}
		if (!status) {
			return <div>Could not load the inventory balance sync status.</div>;
		}

		const { lastRun, queue, coverage, schedule, healthReasons, healthSeverity } = status;

		return (
			<>
				<Typography variant="h5" align="left" gutterBottom style={{ position: 'relative' }}>
					Inventory Balance Sync
					<span style={{ position: 'absolute', right: 0, top: 0 }}>
						<FMPStatusIcon status={healthSeverity} large title={status.health} />
					</span>
				</Typography>

				{!_.isEmpty(healthReasons) && (
					<div className={classes.healthReasons}>
						{healthReasons.map((reason) => (
							<div key={reason}>{reason}</div>
						))}
					</div>
				)}

				<FMPSectionHeader label={'Last Run'} isTop={true} />
				{this.renderRow(
					'Last Run At',
					lastRun.lastRunAt
						? `${moment(lastRun.lastRunAt).format('ddd, M/D h:mm a')} (${formatAgeMinutes(
							lastRun.lastRunAgeMinutes
						)})`
						: 'Never'
				)}
				{this.renderRow('Took', `${lastRun.tookSeconds}s`)}
				{this.renderRow('Terminal-Months Processed', lastRun.terminalMonthsProcessed)}
				{this.renderRow('Periods Written', lastRun.periodsWritten)}
				{this.renderRow('Rollovers Written', lastRun.rolloversWritten)}
				{lastRun.lastError && this.renderRow('Last Error', lastRun.lastError, true)}

				<FMPSectionHeader label={'Recalc Queue'} />
				{this.renderRow('Pending', queue.pendingCount)}
				{this.renderRow('Processing', queue.processingCount)}
				{this.renderRow(
					'Oldest Pending',
					queue.pendingCount > 0 ? formatAgeMinutes(queue.oldestPendingAgeMinutes) : '-'
				)}
				{this.renderRow(
					'Errors (last 7 days)',
					queue.errorCountLast7Days,
					queue.errorCountLast7Days > 0
				)}

				<FMPSectionHeader label={'Coverage'} />
				{this.renderRow('Periods Stored', coverage.periodCount.toLocaleString())}
				{this.renderRow('Terminals', coverage.terminalCount)}
				{this.renderRow(
					'Range',
					coverage.firstPeriod ? `${coverage.firstPeriod} - ${coverage.lastPeriod}` : '-'
				)}
				{this.renderRow('Rows With Warnings', coverage.warningCount, coverage.warningCount > 0)}

				<FMPSectionHeader label={'Run Schedule'} />
				<div className={classes.scheduleNote}>
					Recalculates every {schedule.intervalMinutes} minutes when source data changes, with a
					safety-net sweep every {schedule.nightlySweepHours} hours. Periods before{' '}
					{moment(`${schedule.seedYear}-${schedule.seedMonth}-01`, 'YYYY-M-DD').format('MMMM YYYY')}{' '}
					are settled and are never recomputed.
				</div>

				{canRwContracts && (
					<div style={{ marginTop: 20 }}>
						<Button
							color="primary"
							variant="outlined"
							component={Link}
							to="/inventoryManagement/balanceSync"
						>
							View details
						</Button>
					</div>
				)}
			</>
		);
	};

	render () {
		const {
			classes,
		} = this.props;

		return (
			<Grid container spacing={3}>
				<Grid item xs={12} md={6}>
					<Paper>
						<div className={classes.innerPaper}>{this.renderSyncPanel()}</div>
					</Paper>
				</Grid>
				<Grid item xs={12} md={6}>
					<Paper>
						<div className={classes.innerPaper}>
							<Typography variant="h5" align="left" gutterBottom style={{position:'relative'}}>
								Inventory Management Dashboard
							</Typography>
							<div>
								Future place for inventory overview, stats, graphs, etc..
							</div>
						</div>
					</Paper>
				</Grid>
			</Grid>
		);
	}
}

InventoryDashboard.propTypes = {
	classes: PropTypes.object.isRequired,
	showErrors: PropTypes.func.isRequired,
	canRwContracts: PropTypes.bool,
};

export default _.flow([
	withStyles(styles),
	InventoryBalanceStatusContainer,
])(InventoryDashboard);
